/**
 * `.rl-outcomes` — the selected reel's Reel Options, in the order a player will
 * meet them (CAE-1552).
 *
 * Roles rather than a `<table>`, the way `contests-table.tsx` and the Overview's
 * outcome table both carry them over the same grid-of-divs: the grid is what
 * gives the columns their widths, and the roles are what make them columns to
 * anything not looking at the screen.
 *
 * **Reordering lives here, not on the row.** A drop is a fact about two rows —
 * which one was picked up and which one it landed on — so the row reports its
 * drag and this owns the list. Carried over from the panel this replaced,
 * including the private drag MIME type, which keeps a reel option being dragged
 * from being read as one by any other drop target on the page.
 */
import { useState, type DragEvent } from 'react';
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import type { Asset } from '../../../../api/assets';
import type { ContestDetailReelOption, ContestFixture, ReelKind } from '../../../../api/contests';
import { ReelOptionRow } from './reel-option-row';
import { REEL_OPTION_COLUMNS, REEL_OPTION_TABLE_MIN_WIDTH } from './reel-option-layout';

/** Carried over from `contest-reel-options-panel.tsx` unchanged. */
const REEL_OPTION_DRAG_TYPE = 'application/x-s2w-reel-option-id';

/**
 * The head's labels, each aligned to the cells beneath it — the odds column is
 * right-aligned so its figures line up, and a left-aligned label over it reads
 * as belonging to the column before.
 */
const HEADINGS = [
  { label: '', align: 'left' },
  { label: 'Asset', align: 'center' },
  { label: 'Outcome', align: 'left' },
  { label: 'Odds', align: 'right' },
  { label: 'Status', align: 'left' },
  { label: 'Enabled', align: 'center' },
  { label: '', align: 'right' },
] as const;

export interface ReelOptionTableProps {
  options: readonly ContestDetailReelOption[];
  fixturesById: ReadonlyMap<string, ContestFixture>;
  assetsById: ReadonlyMap<string, Asset>;
  editable: boolean;
  busy: boolean;
  onToggleEnabled: (option: ContestDetailReelOption, enabled: boolean) => void;
  /** Moves an option to a zero-based position on its reel. */
  onMoveTo: (option: ContestDetailReelOption, position: number) => void;
  onPickAsset: (option: ContestDetailReelOption) => void;
  onRemove: (option: ContestDetailReelOption) => void;
  onEditDisplayName: (option: ContestDetailReelOption) => void;
  onEditParticipant: (option: ContestDetailReelOption) => void;
  onClearAsset: (option: ContestDetailReelOption) => void;
  /** The Reel's kind, for which club image stands in for a missing asset (GL-12). */
  reelKind: ReelKind | null;
}

export const ReelOptionTable = ({
  options,
  fixturesById,
  assetsById,
  editable,
  busy,
  onToggleEnabled,
  onMoveTo,
  onPickAsset,
  onRemove,
  onEditDisplayName,
  onEditParticipant,
  onClearAsset,
  reelKind,
}: ReelOptionTableProps) => {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  const onDrop = (targetId: string) => (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const draggedId =
      event.dataTransfer.getData(REEL_OPTION_DRAG_TYPE) || event.dataTransfer.getData('text/plain');
    setDraggingId(null);
    setDropTargetId(null);

    if (!draggedId || draggedId === targetId) return;
    const dragged = options.find((option) => option.id === draggedId);
    const targetIndex = options.findIndex((option) => option.id === targetId);
    if (!dragged || targetIndex === -1) return;

    onMoveTo(dragged, targetIndex);
  };

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box
        role="table"
        aria-label="Reel options"
        sx={(theme: Theme) => ({
          display: 'flex',
          flexDirection: 'column',
          minWidth: `${REEL_OPTION_TABLE_MIN_WIDTH}px`,
          border: `${theme.tokens.border.width} solid ${theme.tokens.color.lineStrong}`,
          borderRadius: `${theme.tokens.radius.insetTile}px`,
          overflow: 'hidden',
          backgroundColor: theme.tokens.color.surface,
        })}
      >
        <Box role="rowgroup">
          <Box
            role="row"
            sx={(theme: Theme) => ({
              display: 'grid',
              gridTemplateColumns: REEL_OPTION_COLUMNS,
              gap: '14px',
              alignItems: 'center',
              padding: '10px 14px',
              backgroundColor: theme.tokens.color.surfaceMuted,
              borderBottom: theme.tokens.border.default,
              fontSize: '10.5px',
              fontWeight: 600,
              color: theme.tokens.color.ink4,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            })}
          >
            {HEADINGS.map((heading, index) => (
              <Box role="columnheader" key={index} sx={{ minWidth: 0, textAlign: heading.align }}>
                {heading.label}
              </Box>
            ))}
          </Box>
        </Box>

        {/*
         * The rows get a group of their own so the row's `:first-of-type` border
         * reset matches the first *row* rather than the head — which is also a
         * div, and also a child of this column. Without it the first row's top
         * border stacks against the head's bottom one as a double rule.
         */}
        <Box role="rowgroup" sx={{ display: 'flex', flexDirection: 'column' }}>
          {options.map((option, index) => (
            <ReelOptionRow
              key={option.id}
              option={option}
              index={index}
              rowCount={options.length}
              fixture={fixturesById.get(option.contestFixtureId)}
              asset={option.assetId ? assetsById.get(option.assetId) : undefined}
              editable={editable}
              busy={busy}
              onToggleEnabled={(enabled) => onToggleEnabled(option, enabled)}
              onMoveTo={(position) => onMoveTo(option, position)}
              onPickAsset={() => onPickAsset(option)}
              onRemove={() => onRemove(option)}
              onEditDisplayName={() => onEditDisplayName(option)}
              onEditParticipant={() => onEditParticipant(option)}
              onClearAsset={() => onClearAsset(option)}
              reelKind={reelKind}
              isDragging={draggingId === option.id}
              isDropTarget={dropTargetId === option.id && draggingId !== option.id}
              gripDragProps={{
                draggable: editable && !busy,
                onDragStart: (event) => {
                  event.dataTransfer.setData(REEL_OPTION_DRAG_TYPE, option.id);
                  event.dataTransfer.setData('text/plain', option.id);
                  event.dataTransfer.effectAllowed = 'move';
                  setDraggingId(option.id);
                },
                onDragEnd: () => {
                  setDraggingId(null);
                  setDropTargetId(null);
                },
              }}
              rowDropProps={{
                onDragOver: (event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  setDropTargetId(option.id);
                },
                onDragLeave: (event) => {
                  // Ignored while the pointer is still inside this row — it is
                  // crossing one of the row's own children, not leaving it.
                  const next = event.relatedTarget as Node | null;
                  if (next && event.currentTarget.contains(next)) return;
                  setDropTargetId(null);
                },
                onDrop: onDrop(option.id),
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};
